/**
 * Logique de submitOrder pour la page /a/[slug] (adhésion).
 * Gère :
 *  - Free order (createFreeOrder)
 *  - Approval flow (require_approval)
 *  - Stripe (createOrder + confirmCardPayment)
 *  - PayPal (createOrder + redirect /payment)
 *
 * Extrait du composant index.vue pour réduire la taille.
 */
import { membershipsApi } from "~/services/api/memberships";
import { commandeParCarte } from "~/utils/commandeParCarte";
import type { CreateMembershipOrderPayload } from "~/types/membership";
import { extractApiError } from "~/utils/apiError";
import { parametresIntegration } from "~/utils/embed";

interface PendingOrderResult {
  order_id: string;
  payment_intent_id: string;
  client_secret: string;
  stripe_account_id?: string;
}

interface CheckoutHandle {
  confirmCardPayment: () => Promise<{ error?: unknown }>;
  isInitialized: { value: boolean };
  /** Champ carte RÉELLEMENT monté (≠ SDK chargé). */
  cardMounted: { value: boolean };
}

export interface UseMembershipOrderSubmitDeps {
  slug: Ref<string>;
  isProcessing: Ref<boolean>;
  totalPrice: Ref<number>;
  isFreeOrder: Ref<boolean>;
  requiresApproval: Ref<boolean>;
  stripeInitError: Ref<boolean>;
  stripeErrorMessage: Ref<string>;
  orderError: Ref<string | null>;
  orderErrorTechnical: Ref<boolean>;
  promoApplied: Ref<{ code: string; discount_type: string; discount_value: number } | null>;
  buyerInfo: Ref<{ firstName: string; lastName: string; email: string; phone: string }>;
  checkout: CheckoutHandle;
  /**
   * Instance PayPal — utilisée quand `usesPaypalCard.value === true`
   * (preferred=paypal ou PayPal-only). Optionnelle pour compat ascendante.
   */
  paypalCheckout?: CheckoutHandle;
  /** Computed indiquant si on doit utiliser PayPal CardFields au lieu de Stripe. */
  usesPaypalCard?: Ref<boolean>;
  setPendingOrderResult: (r: PendingOrderResult) => void;
  clearCheckoutSession: () => void;
  buildOrderPayload: () => CreateMembershipOrderPayload;
  /**
   * Modal de progression (parité /b/[slug] event, Karim 2026-06-21).
   * Optionnels pour compat ascendante : si non fournis, pas de modal.
   */
  processingStep?: Ref<"creating" | "paying" | "confirming" | "success" | "error">;
  processingProgress?: Ref<number>;
  startProgress?: (target: number, duration?: number) => void;
  stopProgress?: () => void;
}

export function useMembershipOrderSubmit(deps: UseMembershipOrderSubmitDeps) {
  const { t } = useI18n();
  const localePath = useLocalePath();
  // Widget chez l'association (`?embed=1&host=…`) : chaque page suivante du
  // tunnel (demande envoyée, paiement, succès) le reste.
  const route = useRoute();
  function setOrderError(e: unknown) {
    const info = extractApiError(e);
    deps.orderError.value = info.message;
    deps.orderErrorTechnical.value = info.isTechnical;
    if (info.isTechnical) console.error("[MembershipOrder] Technical error:", e);
    // Bascule le modal en mode erreur (overlay reste affiché jusqu'au
    // dismiss-error click, parité /b event).
    deps.stopProgress?.();
    if (deps.processingStep) deps.processingStep.value = "error";
  }

  function clearOrderError() {
    deps.orderError.value = null;
    deps.orderErrorTechnical.value = false;
  }

  function buyerEmail() { return deps.buyerInfo.value.email; }
  function buyerFullName() { return `${deps.buyerInfo.value.firstName} ${deps.buyerInfo.value.lastName}`.trim(); }

  /**
   * Demande d'adhésion SUR DEMANDE envoyée : sa propre adresse,
   * `/a/<slug>/demande-envoyee` (Karim 2026-09-29 : « pourquoi l'url ne change
   * pas ? »). Avant, un état de la page du formulaire : la recharger rouvrait
   * un formulaire vide et l'adhérent renvoyait sa demande. `replace` : le
   * retour arrière ne ramène pas au formulaire rempli.
   */
  function navigateToDemandeEnvoyee(orderId: string) {
    navigateTo(
      {
        path: localePath({ path: `/a/${deps.slug.value}/demande-envoyee` }),
        query: {
          order_id: orderId,
          email: buyerEmail(),
          ...parametresIntegration(route.query),
        },
      },
      { replace: true },
    );
  }

  function navigateToSuccess(orderId: string) {
    navigateTo({
      path: localePath({ path: `/a/${deps.slug.value}/success` }),
      query: { order_id: orderId, email: buyerEmail(), ...parametresIntegration(route.query) },
    });
  }

  /** True si le système de paiement n'est pas prêt. */
  function isPaymentSystemNotReady(): boolean {
    if (deps.totalPrice.value > 0 && !deps.requiresApproval.value) {
      // PayPal CardFields → on regarde paypalCheckout init.
      if (deps.usesPaypalCard?.value && deps.paypalCheckout) {
        return !deps.paypalCheckout.cardMounted.value;
      }
      // Sinon → flow Stripe historique.
      return deps.stripeInitError.value || !deps.checkout.cardMounted.value;
    }
    return false;
  }

  /**
   * Flow « require_approval » : free ou payant — la demande attend l'accord
   * de l'association (page « Demande envoyée ») ou est acceptée d'office
   * (page de succès). Dans les deux cas, une page suit.
   */
  async function handleApprovalFlow(payload: CreateMembershipOrderPayload): Promise<void> {
    const isTrulyFreeApproval = deps.isFreeOrder.value && !deps.promoApplied.value;
    const result = isTrulyFreeApproval
      ? await membershipsApi.createFreeOrder(deps.slug.value, payload)
      : await membershipsApi.createOrder(deps.slug.value, payload);
    deps.clearCheckoutSession();
    if (result.status === "pending_approval") {
      navigateToDemandeEnvoyee(result.order_id);
    } else {
      navigateToSuccess(result.order_id);
    }
  }

  // Redirige vers la page /payment PayPal (Connected Path).
  function navigateToPaypalPayment(orderResult: {
    order_id: string;
    paypal_order_id?: string;
    paypal_merchant_id?: string;
    amount_breakdown?: { total?: number };
  }) {
    deps.clearCheckoutSession();
    navigateTo({
      path: localePath({ path: `/a/${deps.slug.value}/payment` }),
      query: {
        order_id: orderResult.order_id,
        paypal_order_id: orderResult.paypal_order_id,
        paypal_merchant_id: orderResult.paypal_merchant_id,
        // Montant que PayPal débitera (fixé par le serveur : adhésion, don,
        // frais couverts, soutien) — jamais le prix de l'adhésion seul.
        amount: String(orderResult.amount_breakdown?.total ?? deps.totalPrice.value),
        email: buyerEmail(),
        name: buyerFullName(),
        payment_provider: "paypal",
        ...parametresIntegration(route.query),
      },
    });
  }

  // Flow payant Stripe : pending order + confirmCardPayment + confirmOrder.
  async function handleStripePayment(orderResult: {
    order_id: string;
    payment_intent_id?: string;
    client_secret?: string;
    stripe_account_id?: string;
  }) {
    deps.setPendingOrderResult({
      order_id: orderResult.order_id,
      payment_intent_id: orderResult.payment_intent_id ?? "",
      client_secret: orderResult.client_secret ?? "",
      stripe_account_id: orderResult.stripe_account_id,
    });

    if (deps.processingStep) deps.processingStep.value = "paying";
    deps.startProgress?.(60);

    const { error } = await deps.checkout.confirmCardPayment();
    if (error) {
      setOrderError(error);
      return;
    }

    if (deps.processingStep) deps.processingStep.value = "confirming";
    deps.startProgress?.(90);

    try {
      await membershipsApi.confirmOrder(orderResult.order_id, orderResult.payment_intent_id);
    } catch {
      // Non-blocking — webhook will also confirm
    }

    deps.stopProgress?.();
    if (deps.processingProgress) deps.processingProgress.value = 100;
    if (deps.processingStep) deps.processingStep.value = "success";

    navigateToSuccess(orderResult.order_id);
  }

  /**
   * Flow payant PayPal CardFields (preferred=paypal). Le PayPal SDK gère
   * lui-même la création de l'order + capture via `createPaymentIntent` +
   * `onCapture` injectés dans le useCheckout PayPal. Donc ici on appelle
   * juste `confirmCardPayment()` qui déclenche `cardFields.submit()` +
   * `onApprove` → capture → redirect successUrl. Pas de createOrder
   * backend séparé à faire ici.
   */
  async function handlePaypalCardPayment() {
    if (!deps.paypalCheckout) {
      setOrderError(new Error(t("adhesion.useMembershipOrderSubmit.paypalNonDisponible")));
      return;
    }
    if (deps.processingStep) deps.processingStep.value = "paying";
    deps.startProgress?.(60);
    const { error } = await deps.paypalCheckout.confirmCardPayment();
    if (error) {
      setOrderError(error);
      return;
    }
    // Le SDK PayPal redirige lui-même vers successUrl après capture.
    deps.stopProgress?.();
    if (deps.processingProgress) deps.processingProgress.value = 100;
    if (deps.processingStep) deps.processingStep.value = "success";
  }

  // Flow payant (non-approval) : free promo → createOrder → Stripe ou PayPal.
  async function handlePaidFlow(payload: CreateMembershipOrderPayload) {
    const isTrulyFree = deps.isFreeOrder.value && !deps.promoApplied.value;

    if (isTrulyFree) {
      const result = await membershipsApi.createFreeOrder(deps.slug.value, payload);
      navigateToSuccess(result.order_id);
      return;
    }

    // Path PayPal CardFields (preferred=paypal) : géré inline par le SDK
    // PayPal (createPaymentIntent + onCapture du useCheckout PayPal).
    // Aucun appel createOrder séparé — le SDK l'a déjà fait via
    // createPaymentIntent quand l'utilisateur a tapé sa carte.
    if (deps.usesPaypalCard?.value) {
      await handlePaypalCardPayment();
      return;
    }

    // Carte Stripe : la commande part TOUJOURS en carte, même si le donateur
    // avait ouvert PayPal avant (cf. commandeParCarte).
    const orderResult = await membershipsApi.createOrder(deps.slug.value, commandeParCarte(payload));
    if (orderResult.free_with_promo) {
      navigateToSuccess(orderResult.order_id);
      return;
    }

    const orderProvider = orderResult.payment_provider as "stripe" | "paypal" | undefined;
    if (orderProvider === "paypal") {
      navigateToPaypalPayment(orderResult);
      return;
    }

    await handleStripePayment(orderResult);
  }

  async function submitOrder() {
    if (deps.isProcessing.value) return;

    if (isPaymentSystemNotReady()) {
      deps.orderError.value = deps.stripeErrorMessage.value || t("adhesion.useMembershipOrderSubmit.systemePaiementPasPret");
      deps.orderErrorTechnical.value = true;
      return;
    }

    deps.isProcessing.value = true;
    if (deps.processingStep) deps.processingStep.value = "creating";
    if (deps.processingProgress) deps.processingProgress.value = 0;
    clearOrderError();
    deps.startProgress?.(25);

    let redirecting = false;
    try {
      const payload = deps.buildOrderPayload();

      if (deps.requiresApproval.value) {
        // 🔴 Une page SUIT toujours la demande (cf. handleApprovalFlow). Avant,
        // « Demande envoyée ! » s'affichait sur place, SOUS la fenêtre « Création
        // de l'adhésion… Veuillez ne pas fermer cette page » restée ouverte à
        // vie : les adhérents refaisaient leur demande — commandes en double
        // (Elite Pétanque, Karim 2026-09-29 ; défaut présent depuis le 22/06).
        await handleApprovalFlow(payload);
        redirecting = true;
        return;
      }

      await handlePaidFlow(payload);
      // Si on arrive ici sans erreur, un navigateTo / SDK redirect est en cours.
      redirecting = true;
    } catch (e: unknown) {
      setOrderError(e);
    } finally {
      // Ne reset PAS isProcessing si le modal est en mode erreur — l'overlay
      // doit rester affiché jusqu'au dismiss-error click. Pareil si on redirect.
      if (!redirecting && deps.processingStep?.value !== "error") {
        deps.isProcessing.value = false;
      }
    }
  }

  return { submitOrder, setOrderError, clearOrderError };
}
